/**
 * Real Liberty Copper — Theme v2 JavaScript
 *
 * Cart helpers, event bus, and the slide-out cart drawer controller.
 * Alpine.js components are inline in their sections; coin-lookup.js is separate.
 */

/* ── Landing-page attribution ──────────────────────────────────────────────
 * Copy the first-touch landing cookies (set by landing-tracker.liquid) onto
 * cart.attributes so they ride checkout → order note_attributes. That's how a
 * paid order is attributed back to its landing page. No-op (returns early) for
 * any session that never landed on a tracked URL. Fire-and-forget; failures
 * never block add-to-cart or the drawer. */
function rlcReadCookie(name) {
  var m = document.cookie.match('(?:^|; )' + name.replace(/([.*+?^${}()|[\]\\])/g, '\\$1') + '=([^;]*)');
  return m ? decodeURIComponent(m[1]) : null;
}
window.rlcStampLanding = function () {
  try {
    var raw = rlcReadCookie('rlc_lp');
    if (!raw) return;                          // not a tracked session
    var d = JSON.parse(raw);
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', accept: 'application/json' },
      body: JSON.stringify({
        attributes: {
          landing_page: d.lp || '',
          landing_url: d.url || '',
          landing_at: d.ts || '',
          landing_vid: rlcReadCookie('rlc_vid') || '',
          utm_source: d.utm_source || '',
          utm_medium: d.utm_medium || '',
          utm_campaign: d.utm_campaign || '',
          utm_term: d.utm_term || '',
          utm_content: d.utm_content || ''
        }
      })
    }).catch(function () {});
  } catch (err) { /* non-blocking */ }
};

/* ── Cart count badge helper ── */
window.updateCartCount = function (count) {
  document.querySelectorAll('[data-cart-count]').forEach(function (el) {
    el.textContent = count;
    el.hidden = count === 0;
  });
};

document.addEventListener('cart:updated', function (e) {
  if (e.detail && typeof e.detail.item_count !== 'undefined') {
    window.updateCartCount(e.detail.item_count);
  }
});

/* ──────────────────────────────────────────────────────────────────────────
 * Cart drawer
 * ──────────────────────────────────────────────────────────────────────── */
const DRAWER_SECTION = 'cart-drawer';

/* Parse a rendered cart-drawer section string and apply it to the live DOM:
 * swap the refreshable content, sync the drawer + header counts. */
function rlcApplyDrawerHTML(html) {
  if (!html) return;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const fresh = doc.querySelector('[data-cart-drawer-content]');
  const live = document.querySelector('[data-cart-drawer-content]');
  if (fresh && live) live.replaceWith(fresh);

  // Header count inside the drawer (outside the swapped content).
  const freshCount = doc.querySelector('[data-cart-drawer-count]');
  const count = freshCount ? parseInt(freshCount.textContent, 10) || 0 : null;
  if (count !== null) {
    document.querySelectorAll('[data-cart-drawer-count]').forEach(function (el) {
      el.textContent = count;
    });
    window.updateCartCount(count);
  }
}

/* Fetch the drawer section fresh (no cart mutation) via Section Rendering API. */
async function rlcFetchDrawer() {
  try {
    const res = await fetch('/?sections=' + DRAWER_SECTION, { headers: { accept: 'application/json' } });
    const data = await res.json();
    return data[DRAWER_SECTION];
  } catch (err) {
    console.error('[RLC] drawer fetch failed:', err);
    return null;
  }
}

function rlcSetDrawerOpenState(isOpen) {
  document.body.classList.toggle('rlc-cart-drawer-open', isOpen);
  document.querySelectorAll('[data-rlc-premium-sticky-atc]').forEach(function (stickyBar) {
    stickyBar.classList.toggle('rlc-cart-drawer-suppressed', isOpen);
  });
}

document.addEventListener('alpine:init', function () {
  window.Alpine.store('cartDrawer', {
    open: false,
    show() { this.open = true; rlcSetDrawerOpenState(true); },
    close() { this.open = false; rlcSetDrawerOpenState(false); },
    // Apply already-rendered section HTML (from an add/change response), then open.
    openWith(html) { rlcApplyDrawerHTML(html); this.open = true; rlcSetDrawerOpenState(true); },
    // Re-fetch fresh, then open (header-icon click).
    async openFresh() { const html = await rlcFetchDrawer(); rlcApplyDrawerHTML(html); this.open = true; rlcSetDrawerOpenState(true); },
    // Apply fresh HTML without changing open state (qty/remove while open).
    apply(html) { rlcApplyDrawerHTML(html); }
  });
});

/* Short customer-facing notice (reservation clamps, failed updates). One
 * polite live region, reused; sits above the drawer and its scrim, at the
 * top so it never covers the sticky checkout bars at the bottom. */
window.rlcNotify = function (message) {
  let el = document.getElementById('rlc-notice');
  if (!el) {
    el = document.createElement('div');
    el.id = 'rlc-notice';
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    el.style.cssText = 'position:fixed;left:50%;top:calc(16px + env(safe-area-inset-top, 0px));transform:translateX(-50%);z-index:10000;' +
      'width:max-content;max-width:calc(100vw - 32px);padding:12px 18px;border-radius:12px;' +
      'background:#1A1A1A;color:#F7F2E8;font:14px/1.4 "Helvetica Neue",Helvetica,Arial,sans-serif;' +
      'box-shadow:0 8px 24px rgba(0,0,0,0.25);transition:opacity .2s;opacity:0;pointer-events:none;';
    document.body.appendChild(el);
  }
  el.textContent = message;
  el.style.opacity = '1';
  clearTimeout(el._rlcHide);
  el._rlcHide = setTimeout(function () { el.style.opacity = '0'; }, 6000);
};

/* Set the coins held under a standard line's `_reservation_token` to `qty`
 * (0 releases them). The server clamps to a per-token cap and can come up
 * short on stock, so `reserved_count` may be less than asked. Resolves to
 * { success, reserved_count, message }, or null when the hold is unknown. */
async function rlcSyncHold(token, qty) {
  const base = window.RLC_PROXY_BASE || '/apps/coin-lookup';
  try {
    const res = await fetch(base + '/coin-reserve-random', {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ token: token, qty: qty })
    });
    if (!res.ok) return null;
    const data = await res.json();
    return typeof data.reserved_count === 'number' ? data : null;
  } catch (err) {
    console.error('[RLC] reservation sync failed:', err);
    return null;
  }
}

function rlcCoins(n) { return n + (n === 1 ? ' coin' : ' coins'); }

/* Re-read the cart and drawer without changing anything, so optimistic UI
 * snaps back to what the cart really holds. */
async function rlcRefreshCart() {
  const [cart, html] = await Promise.all([
    fetch('/cart.js', { headers: { accept: 'application/json' } }).then(function (r) { return r.json(); }),
    rlcFetchDrawer()
  ]);
  if (html && window.Alpine) window.Alpine.store('cartDrawer').apply(html);
  document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart, bubbles: true }));
  return cart;
}

async function rlcApplyLineQty(key, qty, token) {
  let target = qty;
  if (token) {
    const hold = await rlcSyncHold(token, qty);
    if (!hold) {
      // Hold unknown: leave the line as it was rather than let the cart
      // outrun its reservation. A remove still goes through; anything left
      // held expires with the reservation.
      if (qty > 0) {
        window.rlcNotify('We couldn’t update your reservation. Please try again.');
        return rlcRefreshCart();
      }
    } else if (hold.reserved_count < qty) {
      target = hold.reserved_count;
      window.rlcNotify(
        target === 0 ? 'No coins are available right now, so this coin was removed from your cart.'
        : hold.success ? 'You can reserve up to ' + rlcCoins(target) + ' at a time, so your quantity is now ' + target + '.'
        : 'Only ' + rlcCoins(target) + ' available right now, so your quantity is now ' + target + '.'
      );
    }
  }
  const res = await fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', accept: 'application/json' },
    body: JSON.stringify({ id: key, quantity: target, sections: DRAWER_SECTION, sections_url: location.pathname })
  });
  const cart = await res.json();
  if (!res.ok) {
    window.rlcNotify(cart.description || cart.message || 'We couldn’t update your cart. Please try again.');
    return rlcRefreshCart();
  }
  if (cart.sections && cart.sections[DRAWER_SECTION] && window.Alpine) {
    window.Alpine.store('cartDrawer').apply(cart.sections[DRAWER_SECTION]);
  }
  document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart, bubbles: true }));
  return cart;
}

/* Quantity / remove for a cart line, from the drawer or the full cart page.
 * - Standard (random) coins pass their `_reservation_token`: the held-coin
 *   count is re-synced first and the line is set to what the server actually
 *   holds, never more. Serial coins pass no token and skip the re-sync.
 * - Updates the line via /cart/change.js with Section Rendering, so the
 *   drawer re-renders server-side.
 * - Calls for the same line run one at a time, so rapid stepper clicks can't
 *   leave the cart and its reservation out of step.
 * Resolves to the cart as it now stands, or undefined on a network error. */
const rlcLineQueue = {};
window.rlcCartQty = function (key, qty, token) {
  const lane = token || key;   // the token outlives key changes
  const run = (rlcLineQueue[lane] || Promise.resolve()).then(function () {
    return rlcApplyLineQty(key, qty, token);
  }).catch(function (err) {
    console.error('[RLC] cart qty update failed:', err);
  });
  rlcLineQueue[lane] = run;
  return run;
};

/* Alpine data for one line on the full cart page (main-cart-items).
 * `token` is the line's `_reservation_token`; only standard coins (not
 * `serial`) send it to rlcCartQty to re-sync their hold. It also identifies
 * the line: Shopify's line key includes the line's discounts, so crossing a
 * discount threshold re-keys and reprices lines, including ones nobody
 * touched. Every cart:updated re-reads this line's key, qty and price. */
window.rlcCartLine = function (key, qty, token, linePrice, serial) {
  return {
    key: key,
    qty: qty,          // what the stepper shows (optimistic)
    savedQty: qty,     // what the cart last confirmed
    linePrice: linePrice,
    busy: false,
    removing: false,
    init() {
      window.addEventListener('cart:updated', (e) => { if (!this.busy) this.sync(e.detail); });
    },
    sync(cart) {
      if (!cart || !cart.items) return;
      const line = cart.items.find((item) => token
        ? (item.properties || {})._reservation_token === token
        : item.key === this.key);
      if (!line) {
        // Gone from the cart. A line matched only by key may just have been
        // re-keyed, so re-render the page rather than hide it.
        if (!token && !this.removing) location.reload();
        this.removing = true;
        return;
      }
      this.removing = false;
      this.key = line.key;
      this.qty = this.savedQty = line.quantity;
      this.linePrice = (line.final_line_price / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
    },
    async setQty(next) {
      if (this.busy) return;
      next = Math.max(0, parseInt(next, 10) || 0);
      this.busy = true;
      this.qty = next;
      if (next === 0) this.removing = true;
      const cart = await window.rlcCartQty(this.key, next, serial ? '' : token);
      this.busy = false;
      if (!cart || !cart.items) { this.qty = this.savedQty; this.removing = false; return; }
      this.sync(cart);
    }
  };
};

/* Alpine data factory for the per-line reservation hold countdown.
 * Counts down to reserved_at + TTL (default 30 min). Spread into x-data. */
window.rlcHoldTimer = function (reservedAtISO, ttlMinutes) {
  const ttl = (ttlMinutes || 30) * 60 * 1000;
  return {
    holdLabel: '',
    holdExpired: false,
    _holdInt: null,
    startHold() {
      const end = new Date(reservedAtISO).getTime() + ttl;
      const tick = () => {
        const ms = end - Date.now();
        if (ms <= 0) {
          this.holdExpired = true;
          this.holdLabel = '0:00';
          if (this._holdInt) clearInterval(this._holdInt);
          return;
        }
        const m = Math.floor(ms / 60000);
        const s = Math.floor((ms % 60000) / 1000);
        this.holdLabel = m + ':' + (s < 10 ? '0' : '') + s;
      };
      tick();
      this._holdInt = setInterval(tick, 1000);
    }
  };
};
